import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import MaskedInput from 'react-maskedinput';
import { toRequiredLabel } from 'utils';
import HelpTooltip from '../Tooltip/HelpTooltip';
import Loading from '../Loading';
import AutosizeInput from 'react-input-autosize';
import { noop, omit, trim } from 'lodash';
import CalculationCheck from '../CalculationCheck';
import styles from './Field.module.scss';

export default class Field extends Component {
  static propTypes = {
    iconLeft: PropTypes.any,
    fieldClassName: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    onMouseUp: PropTypes.func,
    label: PropTypes.node,
    style: PropTypes.string,
    size: PropTypes.string,
    isDisabled: PropTypes.bool,
    disabled: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    placeholder: PropTypes.string,
    isValid: PropTypes.bool,
    validationMessage: PropTypes.string,
    mask: PropTypes.string,
    isRequired: PropTypes.bool,
    tooltipTitle: PropTypes.any,
    tooltipContent: PropTypes.any,
    readOnly: PropTypes.bool,
    maxLength: PropTypes.number,
    id: PropTypes.string,
    className: PropTypes.string,
    position: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    autoComplete: PropTypes.string,
    placeholderChar: PropTypes.string,
    autoFocus: PropTypes.bool,
    asyncValidating: PropTypes.bool,
    hasHelpWrap: PropTypes.bool,
    isAutosize: PropTypes.bool,
    calculationCheckData: PropTypes.any,
    withoutValidationMessage: PropTypes.bool, // show field error when field not "isValid" and "validationMessage" is empty
    name: PropTypes.string,
    inputClassName: PropTypes.string,
  };

  static defaultProps = {
    isDisabled: false,
    onChange: noop,
    onBlur: noop,
    onFocus: noop,
    mask: null,
    isRequired: false,
    readOnly: false,
    className: 'form_item form_item__part',
    children: null,
    autoComplete: 'on',
    placeholderChar: '_',
    hasHelpWrap: true,
  };

  onChange = (e) => {
    const { onChange } = this.props;
    e.stopPropagation();
    onChange(e);
  };

  onBlur = (e) => {
    const { onBlur } = this.props;
    e.target.value = trim(e.target.value);
    onBlur(e);
  };

  validate() {
    const { isValid, validationMessage } = this.props;

    return validationMessage ? (
      <div className={classnames('form_error', { green: isValid, red: !isValid })}>
        {validationMessage}
      </div>
    ) : null;
  }

  renderTooltip = () => {
    const { tooltipContent } = this.props;
    return tooltipContent ? (
      <HelpTooltip content={tooltipContent} title={this.props.tooltipTitle} />
    ) : null;
  };

  renderMaskedInput(inputProps, mask) {
    return (
      <div key="lkfl-masked-form-input">
        <MaskedInput mask={mask} {...inputProps} />
      </div>
    );
  }

  renderAutosizeInput(inputProps) {
    return (
      <AutosizeInput
        inputClassName={inputProps.className}
        {...omit(inputProps, 'className', 'placeholderChar')}
      />
    );
  }

  renderInput(inputProps) {
    const { children, isAutosize } = this.props;
    if (inputProps.type === 'search') {
      inputProps.type = 'text';
      return (
        <div>
          {isAutosize ? this.renderAutosizeInput(inputProps) : <input {...inputProps} />}
          {children}
        </div>
      );
    }
    return isAutosize ? (
      this.renderAutosizeInput(inputProps)
    ) : (
      <input key="lkfl-form-input" {...inputProps} />
    );
  }

  render() {
    const {
      name,
      style,
      mask,
      isDisabled,
      disabled,
      placeholder,
      label,
      value,
      isRequired,
      onFocus,
      type,
      readOnly,
      maxLength,
      isValid,
      validationMessage,
      className,
      id,
      position,
      children,
      autoComplete,
      autoFocus,
      asyncValidating,
      placeholderChar,
      hasHelpWrap,
      calculationCheckData,
      onKeyDown,
      onMouseUp,
      tooltipContent,
      tooltipTitle,
      withoutValidationMessage,
      inputClassName,
      iconLeft,
      fieldClassName,
    } = this.props;
    const fieldClassNameAgregated = classnames(`form_field ${fieldClassName} ${inputClassName}`, {
      [`form_field__${style}`]: style,
      form_field__disable: isDisabled || disabled,
      form_field__error: (!isValid && validationMessage) || (!isValid && withoutValidationMessage),
    });
    const inputProps = {
      name,
      type,
      className: fieldClassNameAgregated,
      onChange: this.onChange,
      onBlur: this.onBlur,
      onFocus,
      onKeyDown,
      onMouseUp,
      disabled: isDisabled || disabled || asyncValidating ? 'disabled' : false,
      placeholder: placeholder,
      value: !value && value !== 0 ? '' : value,
      readOnly,
      maxLength,
      id,
      autoComplete,
      autoFocus,
    };

    const reserveHelpSpace =
      tooltipContent || tooltipTitle
        ? classnames({ 'help-wrap': hasHelpWrap, [`help-wrap__${position}`]: position })
        : '';
    return (
      <div className={`boo ${className}`}>
        {label ? (
          <div className="form_label">{isRequired && label ? toRequiredLabel(label) : label}</div>
        ) : null}
        <div className={`d-flex align-items-center ${reserveHelpSpace}`}>
          <div className="w-100">
            {mask
              ? this.renderMaskedInput({ ...inputProps, placeholderChar }, mask)
              : this.renderInput(inputProps)}
            {iconLeft && <div className={styles.iconWrapper}>{iconLeft}</div>}
            {asyncValidating ? <Loading /> : null}
          </div>
          <div>{type !== 'search' ? children : null}</div>
          {this.renderTooltip()}
          {calculationCheckData && <CalculationCheck {...calculationCheckData} />}
        </div>
        {this.validate()}
      </div>
    );
  }
}
